// Strings owned by the lifecycle-trend widget. Stage labels mirror the
// canonical §6.3.2 stage names — the stacked-area chart legend reads in
// presentational order (positive → neutral → warning) so the chart's
// visual stack matches the legend top-to-bottom.

import type { MemberLifecycleStage } from "@community-analytics/shared";

export const LIFECYCLE_TREND_STRINGS = {
  title: "Lifecycle distribution",
  subtitle: "Member stages over the last 30 days (UTC)",
  empty: {
    title: "Collecting lifecycle data",
    body: "Stage classification runs once per day. Your trend will populate as snapshots accumulate.",
    /** "5 / 30 days collected" — substituted with daysWithData. */
    progress: (daysWithData: number, totalDays: number): string =>
      `${daysWithData} / ${totalDays} days collected`,
  },
  /**
   * §6.3.2 normalcy banner — surfaced under the chart so a fresh owner
   * sees that 30-50% AT_RISK is normal in large public communities,
   * NOT a defect. Mirrors the PRD interpretation guidance verbatim.
   */
  interpretation:
    "AT_RISK 30–50% is normal in large public communities. Mass lurking is the norm — focus on whether the proportion is drifting over time, not a single day's snapshot.",
  /** Tooltip title — a UTC YYYY-MM-DD date. */
  tooltipDateLabel: (date: string): string => date,
  /** Tooltip line — "Engaged · 12". */
  tooltipStageLabel: (stage: MemberLifecycleStage, count: number): string =>
    `${STAGE_LABELS[stage]} · ${count}`,
  /** Tooltip total — "232 classified members". */
  tooltipTotalLabel: (total: number): string => {
    const word = total === 1 ? "member" : "members";
    return `${total} classified ${word}`;
  },
} as const;

/**
 * Display label per stage. Title-cased so legends and tooltips read like
 * prose. Order in this map is NOT significant — chart series order is
 * driven by `LIFECYCLE_STACK_ORDER` below.
 */
export const STAGE_LABELS: Readonly<Record<MemberLifecycleStage, string>> = {
  NEW: "New",
  ACTIVE: "Active",
  ENGAGED: "Engaged",
  DORMANT: "Dormant",
  AT_RISK: "At risk",
  CHURNED: "Churned",
};

/**
 * Stack order (bottom → top). Positive stages anchor at the bottom so
 * they're visible even when the AT_RISK / CHURNED bands dominate the
 * top of the chart. Mirrors the PRD §6.3.2 health intent: positive
 * signal first, warnings last.
 */
export const LIFECYCLE_STACK_ORDER: ReadonlyArray<MemberLifecycleStage> = [
  "ENGAGED",
  "ACTIVE",
  "NEW",
  "DORMANT",
  "AT_RISK",
  "CHURNED",
];

export type LifecycleTrendStrings = typeof LIFECYCLE_TREND_STRINGS;
